import { useState, useEffect, useRef } from 'react';
import { Usd, PriceCaption } from '@/utils/CurrencyContext';
import { useNavigate } from 'react-router-dom';
import { CourseVisual } from '@/components/CourseVisual';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TabWithTooltip } from '@/components/ui/tab-with-tooltip';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { getUser, removeToken } from '@/utils/auth';
import { toast } from 'sonner';
import { 
  BookOpen, ShoppingBag, TrendingUp, Package, ArrowLeft, 
  LogOut, Bell, Plus, Eye, Edit, Trash2, Clock, CheckCircle,
  XCircle, DollarSign, Users, Mail, User
} from 'lucide-react';
import { motion } from 'framer-motion';

const SellerDashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  
  // Learning tab state
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  
  // Products tab state
  const [myProducts, setMyProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  
  // Stats
  const [stats, setStats] = useState({
    totalCourses: 0,
    totalProducts: 0,
    pendingProducts: 0,
    totalSales: 0
  });
  
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchEnrolledCourses();
    fetchMyProducts();
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const notificationInterval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(notificationInterval);
  }, []);

  const fetchEnrolledCourses = async () => {
    try {
      const res = await api.get('/courses/enrolled');
      setEnrolledCourses(res.data);
      setStats(prev => ({ ...prev, totalCourses: res.data.length }));
    } catch (error) {
      console.error('Failed to fetch enrolled courses:', error);
    } finally {
      setIsLoadingCourses(false);
    }
  };

  const fetchMyProducts = async () => {
    try {
      const res = await api.get('/marketplace/my-products');
      setMyProducts(res.data);
      const pending = res.data.filter(p => !p.is_approved).length;
      setStats(prev => ({ 
        ...prev, 
        totalProducts: res.data.length,
        pendingProducts: pending
      }));
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data.slice(0, 10));
      setUnreadCount(res.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleMarkNotificationRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await api.put('/notifications/mark-all-read');
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleLogout = () => {
    removeToken();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await api.delete(`/marketplace/products/${productId}`);
      toast.success('Product deleted');
      fetchMyProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 border-b shadow-sm" style={{ backgroundColor: '#FFFFFF', borderColor: '#E3E1DB' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="p-1 sm:p-2"
              >
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                  User/Seller Dashboard
                </h1>
                <p className="text-xs sm:text-sm" style={{ color: '#5C635A' }}>
                  Welcome back, {user?.name}
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              {/* Notifications Bell with Dropdown */}
              <div className="relative" ref={notificationRef}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="relative text-xs sm:text-sm"
                  onClick={() => setShowNotifications(!showNotifications)}
                  data-testid="seller-notifications-bell"
                >
                  <Bell className="h-4 w-4 mr-1.5" />
                  <span>Alerts</span>
                  {unreadCount > 0 && (
                    <span 
                      className="absolute -top-1 -right-1 h-5 w-5 rounded-full flex items-center justify-center text-xs font-bold text-white"
                      style={{ backgroundColor: '#D86B47' }}
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
                
                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div 
                    className="fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 mt-2 sm:w-80 max-h-96 overflow-y-auto rounded-lg shadow-lg border z-50"
                    style={{ backgroundColor: '#FFFFFF', borderColor: '#E3E1DB', top: 'auto' }}
                  >
                    <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: '#E3E1DB' }}>
                      <h3 className="font-medium" style={{ color: '#163329' }}>Notifications</h3>
                      {unreadCount > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleMarkAllNotificationsRead}
                          className="text-xs"
                        >
                          Mark all read
                        </Button>
                      )}
                    </div>
                    
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center">
                        <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm" style={{ color: '#5C635A' }}>No notifications</p>
                      </div>
                    ) : (
                      <div className="divide-y" style={{ borderColor: '#E3E1DB' }}>
                        {notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={`p-3 cursor-pointer transition-colors hover:bg-gray-50 ${!notification.read ? 'bg-blue-50/50' : ''}`}
                            onClick={() => {
                              if (!notification.read) {
                                handleMarkNotificationRead(notification.id);
                              }
                              setShowNotifications(false);
                            }}
                          >
                            <div className="flex items-start gap-2">
                              {!notification.read && (
                                <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: '#163329' }}></span>
                              )}
                              <div className={!notification.read ? '' : 'pl-4'}>
                                <p className="font-medium text-sm" style={{ color: '#163329' }}>
                                  {notification.title}
                                </p>
                                <p className="text-xs mt-0.5" style={{ color: '#5C635A' }}>
                                  {notification.message}
                                </p>
                                <p className="text-xs mt-1 opacity-60">
                                  {new Date(notification.created_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                className="text-xs sm:text-sm"
                onClick={() => navigate('/chat')}
              >
                <Mail className="h-4 w-4 mr-1.5" />
                <span>Messages</span>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                className="text-xs sm:text-sm"
                onClick={() => navigate('/seller/profile')}
              >
                <User className="h-4 w-4 mr-1.5" />
                <span>Profile</span>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="text-xs sm:text-sm text-red-600 border-red-200 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4 mr-1.5" />
                <span>Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card style={{ borderColor: '#E3E1DB' }}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: '#E8F5E9' }}>
                    <BookOpen className="h-5 w-5" style={{ color: '#2E7D32' }} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold" style={{ color: '#163329' }}>{stats.totalCourses}</p>
                    <p className="text-xs" style={{ color: '#5C635A' }}>Enrolled Courses</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card style={{ borderColor: '#E3E1DB' }}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: '#E3F2FD' }}>
                    <Package className="h-5 w-5" style={{ color: '#1565C0' }} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold" style={{ color: '#163329' }}>{stats.totalProducts}</p>
                    <p className="text-xs" style={{ color: '#5C635A' }}>My Products</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card style={{ borderColor: '#E3E1DB' }}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: '#FFF3E0' }}>
                    <Clock className="h-5 w-5" style={{ color: '#E65100' }} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold" style={{ color: '#163329' }}>{stats.pendingProducts}</p>
                    <p className="text-xs" style={{ color: '#5C635A' }}>Pending Approval</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card style={{ borderColor: '#E3E1DB' }}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: '#FCE4EC' }}>
                    <DollarSign className="h-5 w-5" style={{ color: '#C2185B' }} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold" style={{ color: '#163329' }}><Usd value={stats.totalSales} /></p>
                    <PriceCaption />
                    <p className="text-xs" style={{ color: '#5C635A' }}>Total Sales</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="learning" className="space-y-6">
          <TabsList className="grid grid-cols-2 w-full max-w-md">
            <TabWithTooltip 
              value="learning" 
              title="My Learning" 
              description="View your enrolled courses and continue learning"
              className="flex items-center gap-2"
            >
              <BookOpen className="h-4 w-4" />
              My Learning
            </TabWithTooltip>
            <TabWithTooltip 
              value="products" 
              title="My Products" 
              description="Manage your product listings in the marketplace"
              className="flex items-center gap-2"
            >
              <ShoppingBag className="h-4 w-4" />
              My Products
            </TabWithTooltip>
          </TabsList>

          {/* Learning Tab */}
          <TabsContent value="learning">
            <Card style={{ borderColor: '#E3E1DB' }}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                  Enrolled Courses
                </CardTitle>
                <Button onClick={() => navigate('/courses')} style={{ backgroundColor: '#163329' }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Browse Courses
                </Button>
              </CardHeader>
              <CardContent>
                {isLoadingCourses ? (
                  <div className="text-center py-8">Loading courses...</div>
                ) : enrolledCourses.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="h-12 w-12 mx-auto mb-4" style={{ color: '#E3E1DB' }} />
                    <h3 className="text-lg font-semibold mb-2" style={{ color: '#163329' }}>No courses yet</h3>
                    <p className="text-sm mb-4" style={{ color: '#5C635A' }}>Start your learning journey by enrolling in a course</p>
                    <Button onClick={() => navigate('/courses')} style={{ backgroundColor: '#163329' }}>
                      Browse Courses
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {enrolledCourses.map((course) => (
                      <div 
                        key={course.id}
                        className="p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow"
                        style={{ borderColor: '#E3E1DB' }}
                        onClick={() => navigate(`/courses/${course.id}`)}
                      >
                        <CourseVisual course={course} className="h-32 rounded-lg mb-3" iconClassName="h-8 w-8" iconColor="rgba(255,255,255,0.6)" bg="#163329" />
                        <h4 className="font-semibold mb-1 line-clamp-1" style={{ color: '#163329' }}>{course.title}</h4>
                        <p className="text-sm mb-2" style={{ color: '#5C635A' }}>By {course.teacher_name || 'Instructor'}</p>
                        <div className="flex items-center gap-2">
                          <div className="h-2 flex-1 rounded-full bg-gray-200">
                            <div 
                              className="h-full rounded-full" 
                              style={{ width: `${course.progress || 0}%`, backgroundColor: '#2E7D32' }}
                            />
                          </div>
                          <span className="text-xs" style={{ color: '#5C635A' }}>{course.progress || 0}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card style={{ borderColor: '#E3E1DB' }}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                  My Products
                </CardTitle>
                <Button onClick={() => navigate('/seller/products')} style={{ backgroundColor: '#163329' }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
              </CardHeader>
              <CardContent>
                {isLoadingProducts ? (
                  <div className="text-center py-8">Loading products...</div>
                ) : myProducts.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="h-12 w-12 mx-auto mb-4" style={{ color: '#E3E1DB' }} />
                    <h3 className="text-lg font-semibold mb-2" style={{ color: '#163329' }}>No products yet</h3>
                    <p className="text-sm mb-4" style={{ color: '#5C635A' }}>Start selling by adding your first product to the Insight Warehouse</p>
                    <Button onClick={() => navigate('/seller/products')} style={{ backgroundColor: '#163329' }}>
                      Add Product
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {myProducts.map((product) => (
                      <div 
                        key={product.id}
                        className="flex items-center gap-4 p-4 rounded-lg border"
                        style={{ borderColor: '#E3E1DB' }}
                      >
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                          {product.images?.[0] ? (
                            <img src={product.images[0]} alt={product.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="h-6 w-6" style={{ color: '#E3E1DB' }} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold truncate" style={{ color: '#163329' }}>{product.title}</h4>
                            <Badge 
                              className="text-xs"
                              style={{ 
                                backgroundColor: product.is_approved ? '#E8F5E9' : '#FFF3E0',
                                color: product.is_approved ? '#2E7D32' : '#E65100'
                              }}
                            >
                              {product.is_approved ? 'Approved' : 'Pending'}
                            </Badge>
                          </div>
                          <p className="text-sm" style={{ color: '#5C635A' }}><Usd value={product.price} /></p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/marketplace/${product.id}`)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/seller/products?edit=${product.id}`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteProduct(product.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default SellerDashboard;
